const API_BASE = process.env.REACT_APP_API_BASE || '';

const DEFAULT_FEATURES = ['JWT Auth', 'Swagger', 'PostgreSQL', 'CORS'];
const DEFAULT_COLOR = '#f0ad4e';

function getAdminToken() {
  return localStorage.getItem('adminToken');
}

function buildAccent(color) {
  return `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)`;
}

// Backend returns: { id (UUID), name, json (String), createdAt, updatedAt }
// We expect `json` to contain a wrapper object with metadata + schema:
//   { description, color, accent, icon, features, schema, builtin }
// If `json` is just the raw schema (no wrapper), we fall back gracefully.
function parseTemplate(dto) {
  let parsed = {};
  try {
    parsed = typeof dto.json === 'string' ? JSON.parse(dto.json) : dto.json || {};
  } catch {
    parsed = {};
  }

  // The schema can live at parsed.schema (frontend save format),
  // parsed.json (backend seed/file format), or parsed itself (raw schema).
  let schema = null;
  let meta = {};
  if (parsed && typeof parsed === 'object') {
    if (parsed.schema && typeof parsed.schema === 'object') {
      schema = parsed.schema;
      meta = parsed;
    } else if (parsed.json && typeof parsed.json === 'object') {
      schema = parsed.json;
      meta = parsed;
    } else if (Array.isArray(parsed.entities)) {
      schema = parsed;
    }
  }

  const displayName = (meta && meta.name) || dto.name || '';
  const color = (meta && meta.color) || DEFAULT_COLOR;
  return {
    id: dto.id,
    name: displayName,
    description: (meta && meta.description) || '',
    color,
    accent: (meta && meta.accent) || buildAccent(color),
    icon: (meta && meta.icon) || 'custom',
    features: (meta && Array.isArray(meta.features)) ? meta.features : DEFAULT_FEATURES,
    builtin: !!(meta && meta.builtin),
    json: schema || {},
    entityCount: (schema && Array.isArray(schema.entities)) ? schema.entities.length : 0,
    custom: !(meta && meta.builtin),
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  };
}

function serializeTemplate(template) {
  const wrapper = {
    description: template.description || '',
    color: template.color || DEFAULT_COLOR,
    accent: template.accent || buildAccent(template.color || DEFAULT_COLOR),
    icon: template.icon || 'custom',
    features: template.features || DEFAULT_FEATURES,
    builtin: !!template.builtin,
    schema: template.json,
  };
  return {
    name: template.name,
    json: JSON.stringify(wrapper),
  };
}

export async function fetchTemplates() {
  const res = await fetch(`${API_BASE}/api/v1/templates`);
  const data = await res.json();
  if (!data.success) throw new Error(data.message || 'Failed to load templates');
  return (data.data || []).map(parseTemplate);
}

export async function fetchTemplateById(id) {
  const res = await fetch(`${API_BASE}/api/v1/templates/${id}`);
  const data = await res.json();
  if (!data.success) throw new Error(data.message || 'Template not found');
  return parseTemplate(data.data);
}

export async function createTemplateApi(template) {
  const token = getAdminToken();
  if (!token) throw new Error('Admin token required');
  const res = await fetch(`${API_BASE}/api/v1/admin/templates`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify(serializeTemplate(template)),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.message || 'Create failed');
  return parseTemplate(data.data);
}

export async function updateTemplateApi(id, template) {
  const token = getAdminToken();
  if (!token) throw new Error('Admin token required');
  const res = await fetch(`${API_BASE}/api/v1/admin/templates/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify(serializeTemplate(template)),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.message || 'Update failed');
  return parseTemplate(data.data);
}

export async function deleteTemplateApi(id) {
  const token = getAdminToken();
  if (!token) throw new Error('Admin token required');
  const res = await fetch(`${API_BASE}/api/v1/admin/templates/${id}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${token}` },
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.message || 'Delete failed');
}
